// Death-condition check. Pure. No React, no broadcasts. GameBoard
// wraps with a sweep that sends each dead minion to its owner's
// Cemetery via sendCardToPile (which fires Deathrite triggers first,
// then performs the move).
//
// ── Sorcery rules (codex "Die", "Power", "Zero") ──
//
// A minion is marked for death "whenever it has damage on it equal
// to or greater than its power" (codex "Die"). In Sorcery, *power*
// is both the attack value and the damage capacity (codex "Power":
// "If it takes 3 or more damage in a turn, it will die").
//
// Two important corollaries:
//   • Effective power is floored at 0. "Units may not have less
//     than zero power" (codex "Power"). A debuff that drives
//     effective power below 0 is treated as 0.
//   • Units with 0 effective power exist in the realm. They are
//     NOT marked for death just for being at 0; per codex "Zero",
//     "Units with 0 power only die if they take damage (1 or more)."
//     This is the rule that keeps Faith Incarnate alive when no
//     Wards are in play.
//
// Death model:
//   damageTaken     = baseLife - currentLife
//                     (currentLife is decremented per damage event;
//                      can go negative. See DAMAGE_TARGET handler.
//                      For a 0-base minion that takes 1 damage,
//                      currentLife = -1 and damageTaken = 1.)
//   effectivePower  = max(0, baseLife + _auraStatDelta)
//   dies when       damageTaken > 0 AND damageTaken >= effectivePower
//
// Avatars are excluded. Their HP lives on the player life tracker.
// Sites have their own death rules (become Rubble) and aren't swept
// here.

import { countsAsMinion } from './typeline';
import { effectiveLife } from './effectiveStats';

/**
 * Find every minion currently marked for death per the Sorcery
 * power/damage rules. Returns an array of cardInstance objects in
 * mesh-iteration order (typically insertion order).
 *
 * @param {Iterable<[any, { userData: { cardInstance } }]>} meshes
 * @returns {Object[]} dead minions to remove this frame
 */
export function findDeadMinions(meshes) {
  const dead = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card) continue;
    // countsAsMinion: Automatons are artifacts that are minions
    // (codex). They die at lethal damage like any minion.
    if (!countsAsMinion(card)) continue;
    if (typeof card.currentLife !== 'number') continue;
    if (typeof card.baseLife !== 'number') continue;

    const damageTaken = card.baseLife - card.currentLife;
    const power = effectiveLife(card);
    if (damageTaken > 0 && damageTaken >= power) dead.push(card);
  }
  return dead;
}
